<template>
    <div class="contact-info">
        <contact-avatar :contact="contact" />
        <contact-full-name :contact="contact" class="contact-info__full-name" />
        <contact-status-v2 :subscriptions="subscriptions" class="contact-info__status" />
        <contact-added-date :contact="contact" class="contact-info__added-date" />
        <contact-entry-point :contact="contact" class="contact-info__entry-point" />

        <label class="contact-info__sticky-number">
            <vx-icon
                name="info-solid"
                color="grey"
                :size="16"
                class="contact-info__sticky-number-icon"
                v-tooltip:overflow="`Outbound messages are forced to come from this number. \n
                Anytime a contact messages inbound a sticky number \n
                will be automatically assigned to ensure they always \n
                receive responses from the same number.`"
            ></vx-icon>

            <span>Sticky Phone Number: {{ state.stickyPhoneNumber?.phone_number || '-' }}</span>
        </label>

        <vx-button
            data-router="client-route"
            class="contact-info__button--edit"
            appearance="primary-dark"
            size="large"
            :has-shadow="true"
            :href="isEditing ? '' : `#/edit/${contact.id}`"
            :disabled="isEditing"
            @click="emit('contact:edit')"
        >Edit Contact</vx-button>

        <div class="contact-info__divider"></div>

        <contact-identity :contact="contact" />

        <div v-if="hasTags" class="contact-info__divider"></div>

        <template v-if="hasTags">
            <header class="contact-info__section-header">Tags</header>

            <div class="contact-info__section-content">
                <vx-badge
                    v-for="tag in state.tags"
                    :key="tag.id"
                    class="contact-info__tag"
                    color="grey"
                    :tooltip="tag.tag_name"
                    tooltipMode="overflow"
                >
                    {{ tag.tag_name }}
                </vx-badge>
            </div>
        </template>

        <div v-if="hasGroups" class="contact-info__divider"></div>

        <template v-if="hasGroups">
            <header class="contact-info__section-header">Groups</header>

            <div class="contact-info__section-content">
                <vx-badge
                    v-for="group in state.groups"
                    :key="group.group_id"
                    class="contact-info__group"
                    color="grey"
                    :tooltip="group.group_id"
                    tooltipMode="overflow"
                >
                    {{ group.group_id}}
                </vx-badge>
            </div>
        </template>

        <div v-if="hasSegments" class="contact-info__divider"></div>

        <template v-if="hasSegments">
            <header class="contact-info__section-header">Segments</header>

            <div class="contact-info__section-content">
                <vx-badge
                    v-for="segment in segments"
                    :key="segment.id"
                    class="contact-info__segment"
                    color="grey"
                    :tooltip="segment.name"
                    tooltipMode="overflow"
                >
                    {{ segment.name }}
                </vx-badge>
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed, inject, reactive } from 'vue';
import { VxBadge, VxIcon, VxButton } from '@voxie/frontend-components-v3';
import ContactAvatar from '../shared/ContactAvatar.vue';
import ContactFullName from '../shared/ContactFullName.vue';
import ContactAddedDate from '../shared/ContactAddedDate.vue';
import ContactStatusV2 from '../shared/ContactStatusV2.vue';
import ContactIdentity from '../shared/ContactIdentity.vue';
import ContactEntryPoint from '../shared/ContactEntryPoint.vue';
import contactsClient from '../../../services/contactsClient';
import { VxTooltip as VTooltip } from '@voxie/frontend-components-v3';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import { useToasted } from '~/composables/useToasted';

const toasted = useToasted();
const FEATURES = inject('FEATURES');

const props = defineProps({
    contact: {
        type: Object,
        required: true,
    },
    segments: {
        type: Array,
        required: true,
    },
    subscriptions: {
        type: Array,
        required: true,
    },
    isEditing: {
        type: Boolean,
    },
});

const state = reactive({
    tags: [],
    groups: [],
    stickyPhoneNumber: null
});

const emit = defineEmits(['contact:edit']);

const hasTags = computed(() => state.tags.length > 0);
const hasGroups = computed(() => FEATURES.contact_groups && state.groups.length > 0);
const hasSegments = computed(() => props.segments.length > 0);

const fetchTags = () => {
    contactsClient.contactTagsList(Spark.state.currentTeam.id, props.contact.id).then(response => {
        state.tags = response.data.data.map(({id, tag_name}) => ({id, tag_name: tag_name.toLowerCase()}));
    }).catch(e => {
        console.error(e)
        if (!e?.response?.status || e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
        }
    });
};

fetchTags();

const fetchGroups = () => {
    if (!FEATURES.contact_groups) {
        return
    }

    contactsClient.listContactGroups(Spark.state.currentTeam.id, props.contact.id).then(response => {
        state.groups = response.data.data.map((g) => ({group_id: g.group_id.toLowerCase()}));
    }).catch(e => {
        console.error(e)
        if (!e?.response?.status || e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
        }
    });
};

fetchGroups();

const fetchStickyPhoneNumber = () => {
    contactsClient.getStickyPhoneNumber(Spark.state.currentTeam.id, props.contact.id).then(response => {
        state.stickyPhoneNumber = response.data
    }).catch(e => {
        console.error(e)
        if (!e?.response?.status || e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
        }
    })
}
fetchStickyPhoneNumber();
</script>

<style lang="scss">
.contact-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
}

.contact-info__full-name {
    margin: 16px auto 0 auto;
}

.contact-info__status {
    margin-top: 16px;
}

.contact-info__added-date {
    margin: 16px auto 0 auto;
}

.contact-info__sticky-number {
    display: flex;
    align-items: center;
    margin-top: 16px;
    margin-bottom: 0;
    font-family: var(--font-open-sans);
    font-weight: var(--weight-regular);
    font-size: 0.8125rem; // 13px
    color: var(--color-grey-600);

    span {
        margin-left: 10px;
        line-height: 20px;
    }
}

.contact-info__sticky-number-icon {
    display: flex;
}

.contact-info__button--edit {
    margin-top: 16px;

    .vx-button__text {
        font-size: 0.75rem;
    }

    &:hover {
        text-decoration: none;
        color: var(--color-white);
    }
}

.contact-info__divider {
    margin: 24px 0;
    border-top: 1px solid var(--color-grey-100);
    width: 100%;
    padding: 0;
}

.contact-info__section-header {
    font-family: var(--font-dm-sans);
    font-size: 0.75rem;
    font-weight: var(--weight-medium);
    color: var(--color-grey);
}

.contact-info__section-content {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 24px;
    max-width: 100%;
    justify-content: center;
}

.contact-info__tag,
.contact-info__segment {
    background-color: var(--color-grey-100);
    padding: 2px 10px;
    border-radius: 10px;
    cursor: default;

    .vx-badge__content {
        font-size: 0.75rem;
        font-weight: var(--weight-regular);
        color: var(--color-grey);
    }
}
</style>
